import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import {
  GraduationCap,
  ArrowLeft,
  ShoppingCart,
  Tag,
  CheckCircle,
  Clock,
  Lock,
  Unlock,
  AlertCircle,
  Loader2,
  BookOpen,
  Calendar,
  CreditCard,
  Package,
  Sparkles
} from 'lucide-react';

const Store = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, getAuthHeaders, refreshUser } = useAuth();
  const [categories, setCategories] = useState([]);
  const [combos, setCombos] = useState([]);
  const [trackAccess, setTrackAccess] = useState([]);
  const [loading, setLoading] = useState(true);
  const [couponCode, setCouponCode] = useState('');
  const [redeemLoading, setRedeemLoading] = useState(false);
  const [purchaseLoading, setPurchaseLoading] = useState(null);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [validatedCoupon, setValidatedCoupon] = useState(null);
  const [validatingCoupon, setValidatingCoupon] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  
  // Combo selection state - flexible course selection
  const [selectedComboCourse1, setSelectedComboCourse1] = useState('');
  const [selectedComboCourse2, setSelectedComboCourse2] = useState('');
  const [comboLoading, setComboLoading] = useState(false);

  useEffect(() => {
    fetchData();
    // Check if returning from Stripe
    const sessionId = searchParams.get('session_id');
    const success = searchParams.get('success');
    if (sessionId && success === 'true') {
      pollPaymentStatus(sessionId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    
    if (attempts >= maxAttempts) {
      toast.error('Payment could not be confirmed. Please try again.');
      setCheckingPayment(false);
      return;
    }

    setCheckingPayment(true);
    
    try {
      const response = await fetch(`${API}/payments/status/${sessionId}`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });

      const data = await response.json();

      if (data.payment_status === 'paid') {
        toast.success(data.message || 'Payment successful!');
        setCheckingPayment(false);
        fetchData();
        if (refreshUser) refreshUser();
        // Clear URL params
        navigate('/store', { replace: true });
        return;
      } else if (data.status === 'expired') {
        toast.error('Payment session expired');
        setCheckingPayment(false);
        navigate('/store', { replace: true });
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (error) {
      console.error('Payment status error:', error);
      toast.error('Error verifying payment');
      setCheckingPayment(false);
    }
  };

  const handlePurchase = async (trackId) => {
    if (!acceptedTerms) {
      toast.error('Please accept the Terms of Use and Privacy Policy before purchasing');
      return;
    }
    
    setPurchaseLoading(trackId);
    try {
      const response = await fetch(`${API}/payments/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        credentials: 'include',
        body: JSON.stringify({
          track: trackId,
          origin_url: window.location.origin
        })
      });

      // Handle the response based on status
      if (response.status === 403) {
        toast.error('Please register to purchase a course');
        setPurchaseLoading(null);
        return;
      }

      if (!response.ok) {
        let errorMessage = 'Error creating payment';
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || errorMessage;
        } catch (e) {
          // Ignore JSON parse errors for error responses
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      
      // Redirect to Stripe checkout
      window.location.href = data.checkout_url;
    } catch (error) {
      if (error.message !== 'Please register to purchase a course') {
        toast.error(error.message || 'Error creating payment');
      }
      setPurchaseLoading(null);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [categoriesRes, accessRes, combosRes] = await Promise.all([
        fetch(`${API}/categories`, {
          credentials: 'include',
          headers: getAuthHeaders()
        }),
        fetch(`${API}/user/track-access`, {
          credentials: 'include',
          headers: getAuthHeaders()
        }),
        fetch(`${API}/payments/combos`, {
          credentials: 'include',
          headers: getAuthHeaders()
        })
      ]);

      if (categoriesRes.ok) {
        const data = await categoriesRes.json();
        setCategories(data.categories || []);
      }

      if (accessRes.ok) {
        const data = await accessRes.json();
        setTrackAccess(data.track_access || []);
      }

      if (combosRes.ok) {
        const data = await combosRes.json();
        setCombos(data.combos || []);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Error loading data');
    } finally {
      setLoading(false);
    }
  };

  const handleRedeemCoupon = async () => {
    if (!couponCode.trim()) {
      toast.error('Please enter a coupon code');
      return;
    }

    setRedeemLoading(true);
    try {
      // First validate the coupon
      const validateResponse = await fetch(`${API}/coupons/validate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        credentials: 'include',
        body: JSON.stringify({ code: couponCode })
      });

      let validateData;
      try {
        validateData = await validateResponse.json();
      } catch (e) {
        throw new Error('Invalid coupon code');
      }

      if (!validateResponse.ok) {
        throw new Error(validateData.detail || 'Invalid coupon code');
      }

      // If 100% discount, redeem directly
      if (validateData.is_free) {
        const response = await fetch(`${API}/coupons/redeem`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeaders()
          },
          credentials: 'include',
          body: JSON.stringify({ code: couponCode })
        });

        let data;
        try {
          data = await response.json();
        } catch (e) {
          throw new Error('Failed to redeem');
        }

        if (!response.ok) {
          throw new Error(data.detail || 'Failed to redeem');
        }

        toast.success(data.message);
        setCouponCode('');
        setValidatedCoupon(null);
        fetchData();
        if (refreshUser) refreshUser();
      } else {
        // Show discount info and prompt for payment
        setValidatedCoupon(validateData);
        toast.success(`Coupon validated: ${validateData.discount_percent}% discount on ${validateData.track}!`);
      }
    } catch (error) {
      toast.error(error.message || 'Invalid coupon code');
      setValidatedCoupon(null);
    } finally {
      setRedeemLoading(false);
    }
  };

  const handlePurchaseWithCoupon = async () => {
    if (!validatedCoupon) return;
    
    setPurchaseLoading(validatedCoupon.track);
    try {
      const response = await fetch(`${API}/payments/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        credentials: 'include',
        body: JSON.stringify({
          track: validatedCoupon.track,
          origin_url: window.location.origin,
          coupon_code: validatedCoupon.code
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Error creating payment');
      }

      // Redirect to Stripe checkout
      window.location.href = data.checkout_url;
    } catch (error) {
      toast.error(error.message);
      setPurchaseLoading(null);
    }
  };

  const handleComboCheckout = async () => {
    if (!acceptedTerms) {
      toast.error('Please accept the Terms of Use and Privacy Policy before purchasing');
      return;
    }
    
    if (!user || user.is_guest) {
      toast.error('Please log in to purchase a combo package');
      return;
    }

    // Validate that two different courses are selected
    if (!selectedComboCourse1 || !selectedComboCourse2) {
      toast.error('Please select two courses for your combo package');
      return;
    }
    
    if (selectedComboCourse1 === selectedComboCourse2) {
      toast.error('Please select two different courses');
      return;
    }

    // Check if user already has access to selected tracks
    if (hasAccess(selectedComboCourse1)) {
      toast.error(`You already have access to ${selectedComboCourse1}`);
      return;
    }
    if (hasAccess(selectedComboCourse2)) {
      toast.error(`You already have access to ${selectedComboCourse2}`);
      return;
    }

    setComboLoading(true);
    try {
      const response = await fetch(`${API}/payments/combo-checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        credentials: 'include',
        body: JSON.stringify({
          course_1: selectedComboCourse1,
          course_2: selectedComboCourse2,
          origin_url: window.location.origin,
          coupon_code: validatedCoupon && !validatedCoupon.is_free ? validatedCoupon.code : null
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Error creating payment');
      }

      // Redirect to Stripe checkout
      window.location.href = data.checkout_url;
    } catch (error) {
      toast.error(error.message);
    } finally {
      setComboLoading(false);
    }
  };

  const hasAccess = (trackId) => {
    return trackAccess.some(a => a.track === trackId && a.is_active);
  };

  const getAccessInfo = (trackId) => {
    return trackAccess.find(a => a.track === trackId && a.is_active);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950">
        <Loader2 className="w-8 h-8 animate-spin text-[#0047AB]" />
      </div>
    );
  }

  // Separate by group
  const pmiTracks = categories.filter(c => c.group === 'PMI');
  const scrumTracks = categories.filter(c => c.group === 'Scrum');
  const customTracks = categories.filter(c => c.group === 'Custom');

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 pb-20">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/90 dark:bg-slate-900/90 backdrop-blur-xl border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={() => navigate('/dashboard')} data-testid="back-btn">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <h1 className="font-semibold text-lg flex items-center gap-2">
                <ShoppingCart className="w-5 h-5" />
                Courses & Coupons
              </h1>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8">
        {/* Terms and Conditions */}
        <Card className="bg-white dark:bg-slate-900 border-2 border-slate-200 dark:border-slate-700 mb-8">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <Checkbox 
                id="store-terms" 
                checked={acceptedTerms}
                onCheckedChange={setAcceptedTerms}
                className="mt-1"
                data-testid="store-terms-checkbox"
              />
              <Label htmlFor="store-terms" className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed cursor-pointer">
                I agree to the{' '}
                <Link to="/terms" target="_blank" className="text-[#0047AB] hover:underline font-medium">Terms of Use</Link>,{' '}
                <Link to="/privacy-policy" target="_blank" className="text-[#0047AB] hover:underline font-medium">Privacy Policy</Link>, and{' '}
                <Link to="/disclaimer" target="_blank" className="text-[#0047AB] hover:underline font-medium">Disclaimer</Link>.
                I understand that this is an independent exam preparation platform and that access is granted for the duration specified with each product.
                <strong className="block mt-2 text-slate-700 dark:text-slate-300">
                  No refunds are available once access has been granted.
                </strong>
              </Label>
            </div>
          </CardContent>
        </Card>

        {/* Coupon Redemption */}
        <Card className="bg-gradient-to-br from-[#0047AB] to-[#003380] text-white border-0 mb-8">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center">
                <Tag className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="font-bold text-xl">Redeem Coupon</h2>
                <p className="text-blue-100 text-sm">Have a coupon code? Redeem it here.</p>
              </div>
            </div>
            
            <div className="flex gap-3">
              <div className="flex-1">
                <Input
                  value={couponCode}
                  onChange={(e) => {
                    setCouponCode(e.target.value.toUpperCase());
                    setValidatedCoupon(null);
                  }}
                  placeholder="e.g. PMP-ABC123"
                  className="bg-white/10 border-white/20 text-white placeholder:text-white/50 h-12"
                  data-testid="coupon-input"
                />
              </div>
              <Button
                onClick={handleRedeemCoupon}
                disabled={redeemLoading || !couponCode.trim()}
                className="bg-white text-[#0047AB] hover:bg-blue-50 h-12 px-6"
                data-testid="redeem-coupon-btn"
              >
                {redeemLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Validate
                  </>
                )}
              </Button>
            </div>

            {/* Validated Coupon Info */}
            {validatedCoupon && (
              <div className="mt-4 p-4 bg-white/10 rounded-xl">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-lg">
                      {validatedCoupon.discount_percent}% discount on {validatedCoupon.track}
                    </p>
                    <div className="flex items-center gap-3 mt-1">
                      <span className="text-white/70 line-through">€{validatedCoupon.original_price.toFixed(2)}</span>
                      <span className="text-2xl font-bold text-green-300">€{validatedCoupon.final_price.toFixed(2)}</span>
                      <span className="text-white/70">for 6 months</span>
                    </div>
                  </div>
                  <Button
                    onClick={handlePurchaseWithCoupon}
                    disabled={purchaseLoading === validatedCoupon.track}
                    className="bg-green-500 hover:bg-green-600 text-white h-12 px-6"
                    data-testid="purchase-with-coupon-btn"
                  >
                    {purchaseLoading === validatedCoupon.track ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <CreditCard className="w-4 h-4 mr-2" />
                    )}
                    Buy Now
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Combo Packages */}
        <Card className="relative bg-gradient-to-br from-purple-600 to-indigo-700 text-white border-0 mb-8 overflow-hidden">
          <Badge className="absolute top-4 right-4 bg-amber-400 text-amber-900 font-bold px-3 py-1">
            <Sparkles className="w-3 h-3 mr-1" />
            SAVE €9
          </Badge>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-xl">
              <Package className="w-6 h-6" />
              Combo Package
            </CardTitle>
            <p className="text-purple-200 text-sm">
              Choose any two courses and save! Combine your certifications for only €49
            </p>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {/* First Course Selection */}
              <div className="space-y-2">
                <Label className="text-purple-200">Select First Course:</Label>
                <Select value={selectedComboCourse1} onValueChange={setSelectedComboCourse1}>
                  <SelectTrigger className="bg-white/10 border-white/20 text-white">
                    <SelectValue placeholder="Choose a course..." />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.filter(cat => cat.id !== selectedComboCourse2 && cat.is_available !== false).map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>
                        {cat.id} - {cat.name} {hasAccess(cat.id) ? '(owned)' : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Second Course Selection */}
              <div className="space-y-2">
                <Label className="text-purple-200">Select Second Course:</Label>
                <Select value={selectedComboCourse2} onValueChange={setSelectedComboCourse2}>
                  <SelectTrigger className="bg-white/10 border-white/20 text-white">
                    <SelectValue placeholder="Choose a course..." />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.filter(cat => cat.id !== selectedComboCourse1 && cat.is_available !== false).map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>
                        {cat.id} - {cat.name} {hasAccess(cat.id) ? '(owned)' : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex items-center justify-between p-4 bg-white/10 rounded-xl">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-purple-200 line-through">€58</span>
                  <span className="text-3xl font-bold">€49</span>
                  <Badge className="bg-white/20 text-white text-xs">6 Months Access</Badge>
                </div>
                <p className="text-purple-200 text-sm">
                  {selectedComboCourse1 && selectedComboCourse2 
                    ? `${selectedComboCourse1} + ${selectedComboCourse2}`
                    : 'Select two courses above'}
                </p>
              </div>
              <Button
                onClick={handleComboCheckout}
                disabled={comboLoading || !selectedComboCourse1 || !selectedComboCourse2 || selectedComboCourse1 === selectedComboCourse2}
                className="bg-white text-purple-700 hover:bg-purple-50 h-12 px-6 font-semibold"
                data-testid="combo-checkout-btn"
              >
                {comboLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <ShoppingCart className="w-4 h-4 mr-2" />
                )}
                Buy Combo
              </Button>
            </div>

            {(!selectedComboCourse1 || !selectedComboCourse2) && (
              <p className="text-purple-200 text-sm mt-3 flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                Select two different courses to unlock the combo discount.
              </p>
            )}
          </CardContent>
        </Card>

        {/* Active Subscriptions */}
        {trackAccess.length > 0 && (
          <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 mb-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-green-600">
                <Unlock className="w-5 h-5" />
                Your Active Access
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {trackAccess.map((access, idx) => (
                  <div 
                    key={idx}
                    className="flex items-center justify-between p-4 bg-green-50 dark:bg-green-950/20 rounded-xl border border-green-200 dark:border-green-900"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-green-500 flex items-center justify-center">
                        <CheckCircle className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="font-semibold">{access.track}</p>
                        <p className="text-sm text-slate-600 dark:text-slate-400">
                          Access via Coupon
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <Badge variant="outline" className="border-green-500 text-green-600">
                        <Clock className="w-3 h-3 mr-1" />
                        {access.days_remaining} days remaining
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* PMI Tracks */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-[#0047AB]" />
            PMI Certifications
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {pmiTracks.map((track) => {
              const owned = hasAccess(track.id);
              const accessInfo = getAccessInfo(track.id);
              const isAvailable = track.is_available;

              return (
                <Card 
                  key={track.id}
                  className={`bg-white dark:bg-slate-900 border-2 transition-all ${
                    owned 
                      ? 'border-green-500' 
                      : !isAvailable 
                        ? 'border-slate-300 dark:border-slate-700 opacity-75'
                        : 'border-slate-200 dark:border-slate-800 hover:border-[#0047AB]'
                  }`}
                  data-testid={`track-card-${track.id}`}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-bold text-lg">{track.name}</h4>
                          {owned && (
                            <Badge className="bg-green-500 text-white">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Unlocked
                            </Badge>
                          )}
                          {!isAvailable && !owned && (
                            <Badge variant="outline" className="border-amber-500 text-amber-600">
                              <AlertCircle className="w-3 h-3 mr-1" />
                              Coming Soon
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-slate-600 dark:text-slate-400">{track.description}</p>
                      </div>
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        owned ? 'bg-green-100 dark:bg-green-950/30' : 'bg-[#0047AB]/10'
                      }`}>
                        {owned ? (
                          <Unlock className="w-6 h-6 text-green-600" />
                        ) : (
                          <Lock className="w-6 h-6 text-[#0047AB]" />
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-4 text-sm text-slate-500 mb-4">
                      <span className="flex items-center gap-1">
                        <BookOpen className="w-4 h-4" />
                        {track.questions} Questions
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {track.time} Min
                      </span>
                    </div>

                    {owned ? (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-green-600 flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {accessInfo?.days_remaining} days remaining
                        </span>
                        <Button 
                          onClick={() => navigate('/training')}
                          className="bg-green-600 hover:bg-green-700"
                          data-testid={`start-${track.id}-btn`}
                        >
                          Start Training
                        </Button>
                      </div>
                    ) : !isAvailable ? (
                      <div className="p-3 bg-amber-50 dark:bg-amber-950/20 rounded-lg">
                        <p className="text-sm text-amber-700 dark:text-amber-400">
                          {track.unavailable_message || 'This course is currently not available.'}
                        </p>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="text-2xl font-bold text-[#0047AB]">€{track.price}</span>
                          <span className="text-sm text-slate-500 ml-1">/ 6 months</span>
                        </div>
                        <Button
                          className="bg-[#0047AB] hover:bg-[#003380] text-white"
                          onClick={() => handlePurchase(track.id)}
                          disabled={purchaseLoading === track.id || checkingPayment}
                          data-testid={`buy-${track.id}-btn`}
                        >
                          {purchaseLoading === track.id ? (
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          ) : (
                            <CreditCard className="w-4 h-4 mr-2" />
                          )}
                          Buy Now
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Scrum Tracks */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-green-600" />
            Scrum.org Certifications
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {scrumTracks.map((track) => {
              const owned = hasAccess(track.id);
              const accessInfo = getAccessInfo(track.id);
              const isAvailable = track.is_available;

              return (
                <Card 
                  key={track.id}
                  className={`bg-white dark:bg-slate-900 border-2 transition-all ${
                    owned 
                      ? 'border-green-500' 
                      : !isAvailable 
                        ? 'border-slate-300 dark:border-slate-700 opacity-75'
                        : 'border-slate-200 dark:border-slate-800 hover:border-green-600'
                  }`}
                  data-testid={`track-card-${track.id}`}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-bold text-lg">{track.name}</h4>
                          {owned && (
                            <Badge className="bg-green-500 text-white">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Freigeschaltet
                            </Badge>
                          )}
                          {!isAvailable && !owned && (
                            <Badge variant="outline" className="border-amber-500 text-amber-600">
                              <AlertCircle className="w-3 h-3 mr-1" />
                              Coming Soon
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-slate-600 dark:text-slate-400">{track.description}</p>
                      </div>
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        owned ? 'bg-green-100 dark:bg-green-950/30' : 'bg-green-600/10'
                      }`}>
                        {owned ? (
                          <Unlock className="w-6 h-6 text-green-600" />
                        ) : (
                          <Lock className="w-6 h-6 text-green-600" />
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-4 text-sm text-slate-500 mb-4">
                      <span className="flex items-center gap-1">
                        <BookOpen className="w-4 h-4" />
                        {track.questions} Questions
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {track.time} Min
                      </span>
                    </div>

                    {owned ? (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-green-600 flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {accessInfo?.days_remaining} days remaining
                        </span>
                        <Button 
                          onClick={() => navigate('/training')}
                          className="bg-green-600 hover:bg-green-700"
                          data-testid={`start-${track.id}-btn`}
                        >
                          Start Training
                        </Button>
                      </div>
                    ) : !isAvailable ? (
                      <div className="p-3 bg-amber-50 dark:bg-amber-950/20 rounded-lg">
                        <p className="text-sm text-amber-700 dark:text-amber-400">
                          {track.unavailable_message || 'This course is currently not available.'}
                        </p>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="text-2xl font-bold text-green-600">€{track.price}</span>
                          <span className="text-sm text-slate-500 ml-1">/ 6 months</span>
                        </div>
                        <Button
                          className="bg-green-600 hover:bg-green-700 text-white"
                          onClick={() => handlePurchase(track.id)}
                          disabled={purchaseLoading === track.id || checkingPayment}
                          data-testid={`buy-${track.id}-btn`}
                        >
                          {purchaseLoading === track.id ? (
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          ) : (
                            <CreditCard className="w-4 h-4 mr-2" />
                          )}
                          Buy Now
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Custom Tracks */}
        {customTracks.length > 0 && (
          <div className="mb-8">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-indigo-600" />
              Additional Certifications
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {customTracks.map((track) => {
                const owned = hasAccess(track.id);
                const accessInfo = getAccessInfo(track.id);
                const isAvailable = track.is_available;

                return (
                  <Card 
                    key={track.id}
                    className={`bg-white dark:bg-slate-900 border-2 transition-all ${
                      owned 
                        ? 'border-green-500' 
                        : !isAvailable 
                          ? 'border-slate-300 dark:border-slate-700 opacity-75'
                          : 'border-slate-200 dark:border-slate-800 hover:border-indigo-600'
                    }`}
                    data-testid={`track-card-${track.id}`}
                  >
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-bold text-lg">{track.name}</h4>
                            {owned && (
                              <Badge className="bg-green-500 text-white">
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Unlocked
                              </Badge>
                            )}
                            {!isAvailable && !owned && (
                              <Badge variant="outline" className="border-amber-500 text-amber-600">
                                <AlertCircle className="w-3 h-3 mr-1" />
                                Coming Soon
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-slate-600 dark:text-slate-400">{track.description}</p>
                        </div>
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                          owned ? 'bg-green-100 dark:bg-green-950/30' : 'bg-indigo-600/10'
                        }`}>
                          {owned ? (
                            <Unlock className="w-6 h-6 text-green-600" />
                          ) : (
                            <Lock className="w-6 h-6 text-indigo-600" />
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-4 text-sm text-slate-500 mb-4">
                        <span className="flex items-center gap-1">
                          <BookOpen className="w-4 h-4" />
                          {track.questions} Questions
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {track.time} Min
                        </span>
                      </div>

                      {owned ? (
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-green-600 flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {accessInfo?.days_remaining} days remaining
                          </span>
                          <Button 
                            onClick={() => navigate('/training')}
                            className="bg-green-600 hover:bg-green-700"
                            data-testid={`start-${track.id}-btn`}
                          >
                            Start Training
                          </Button>
                        </div>
                      ) : !isAvailable ? (
                        <div className="p-3 bg-amber-50 dark:bg-amber-950/20 rounded-lg">
                          <p className="text-sm text-amber-700 dark:text-amber-400">
                            {track.unavailable_message || 'This course is currently not available.'}
                          </p>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between">
                          <div>
                            <span className="text-2xl font-bold text-indigo-600">€{track.price}</span>
                            <span className="text-sm text-slate-500 ml-1">/ 6 months</span>
                          </div>
                          <Button
                            className="bg-indigo-600 hover:bg-indigo-700 text-white"
                            onClick={() => handlePurchase(track.id)}
                            disabled={purchaseLoading === track.id || checkingPayment}
                            data-testid={`buy-${track.id}-btn`}
                          >
                            {purchaseLoading === track.id ? (
                              <Loader2 className="w-4 h-4 animate-spin mr-2" />
                            ) : (
                              <CreditCard className="w-4 h-4 mr-2" />
                            )}
                            Buy Now
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        )}

        {/* Info Card */}
        <Card className="bg-slate-100 dark:bg-slate-800/50 border-0">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-950/30 flex items-center justify-center shrink-0">
                <AlertCircle className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h4 className="font-semibold mb-1">Purchase Information</h4>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  After purchase, you will receive 6 months access to all questions and practice materials of the selected course. 
                  You can also use a coupon code to get instant access.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default Store;
